import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import { z } from "zod";
import { semanticColor } from "@how/design-system";
import { isPrivilegedRole } from "./role-catalog";
import { RoleChoices } from "./RoleChoices";
import { describeInviteError, useInviteMember, type CustomRole } from "./team-queries";

const inviteSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "An email address is needed")
    .pipe(z.email("Enter an email address")),
  roleKeys: z
    .array(z.string())
    .min(1, "Choose at least one role")
    .max(8, "Choose up to eight roles"),
  campusIds: z.array(z.string()),
});

export interface InviteMemberModalProps {
  readonly opened: boolean;
  readonly customRoles: readonly CustomRole[];
  readonly campuses: readonly {
    readonly id: string;
    readonly name: string;
    readonly isActive: boolean;
  }[];
  readonly multiCampus: boolean;
  /** Only an owner invites someone as an owner. */
  readonly readerIsOwner: boolean;
  readonly onClose: () => void;
}

/**
 * Invite someone with their roles chosen up front (and, in a multi-campus church, their campuses). The roles wait on
 * the invited login and are theirs once they accept; the email's link opens the dashboard for a staff role, the member
 * portal otherwise. Owner only for Owner, as everywhere; the database refuses the same.
 */
export function InviteMemberModal({
  opened,
  customRoles,
  campuses,
  multiCampus,
  readerIsOwner,
  onClose,
}: InviteMemberModalProps) {
  const invite = useInviteMember();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { email: "", roleKeys: ["staff"], campusIds: [] as string[] },
    validate: zod4Resolver(inviteSchema),
  });
  const becomesPrivileged = form.values.roleKeys.some(isPrivilegedRole);
  const campusOptions = campuses
    .filter((c) => c.isActive)
    .map((c) => ({ value: c.id, label: c.name }));

  const close = () => {
    form.reset();
    setError(null);
    onClose();
  };

  const submit = form.onSubmit((values) => {
    setError(null);
    const email = values.email.trim();
    invite.mutate(
      {
        email,
        roleKeys: values.roleKeys,
        campusIds: multiCampus && !becomesPrivileged ? values.campusIds : [],
      },
      {
        onSuccess: (outcome) => {
          notifications.show({
            color: outcome.campusesSet ? semanticColor("success") : semanticColor("warning"),
            title: "Invitation sent",
            message: outcome.campusesSet
              ? `${email} gets an email to accept. Their roles apply once they do.`
              : `${email} gets an email to accept, but their campuses were not set. Open them in the list to choose campuses.`,
          });
          close();
        },
        onError: (e) => {
          setError(describeInviteError(e));
        },
      },
    );
  });

  return (
    <Modal opened={opened} onClose={close} title="Invite someone" radius="lg" size="lg">
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            They get an email to set up their login. Someone who has been removed can be invited
            again here.
          </Text>
          <TextInput
            label="Email"
            type="email"
            autoComplete="off"
            required
            data-autofocus
            {...form.getInputProps("email")}
          />
          <RoleChoices
            value={form.values.roleKeys}
            onChange={(keys) => {
              form.setFieldValue("roleKeys", keys);
            }}
            customRoles={customRoles}
            offerOwner={readerIsOwner}
            disabled={invite.isPending}
            description="Choose what they can do once they accept. Member alone opens only the member portal."
            error={form.errors["roleKeys"]}
          />
          {multiCampus ? (
            <Stack gap={6}>
              <Title order={3} size="h5">
                Campuses
              </Title>
              {becomesPrivileged ? (
                <Text size="sm" c="dimmed">
                  Owners and administrators always see every campus, so there is nothing to choose.
                </Text>
              ) : (
                <MultiSelect
                  label="Works with"
                  description="Leave empty for every campus. The people directory stays church-wide."
                  placeholder={form.values.campusIds.length === 0 ? "Every campus" : undefined}
                  data={campusOptions}
                  clearable
                  searchable
                  {...form.getInputProps("campusIds")}
                />
              )}
            </Stack>
          ) : null}
          {error === null ? null : (
            <Alert color={semanticColor("danger")} variant="light" role="alert">
              {error}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={close} disabled={invite.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={invite.isPending}>
              Send invitation
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
